import { useEffect, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useUpdateReceptionPlant } from "~/hooks/purchasing";
import type { Plant, PurchaseOrderReception } from "~/types/purchasing";
import { PLANT_OPTIONS } from "~/types/purchasing";

interface EditReceptionPlantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reception: PurchaseOrderReception;
  purchaseOrderId: string;
}

export function EditReceptionPlantDialog({
  open,
  onOpenChange,
  reception,
  purchaseOrderId,
}: EditReceptionPlantDialogProps) {
  const [plant, setPlant] = useState<Plant>(reception.plant);
  const updatePlant = useUpdateReceptionPlant();

  useEffect(() => {
    if (open) setPlant(reception.plant);
  }, [open, reception.plant]);

  const disabled =
    plant === reception.plant || updatePlant.isPending;

  const handleSubmit = async () => {
    try {
      await updatePlant.mutateAsync({
        receptionId: reception.id,
        purchaseOrderId,
        newPlant: plant,
      });
      onOpenChange(false);
    } catch {
      // toast handled in hook onError
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modificar planta de recepción</DialogTitle>
          <DialogDescription>
            Esto recalculará el stock entre plantas. No se eliminan movimientos
            de la auditoría.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center gap-2">
            <Label className="w-28">Planta actual:</Label>
            <span className="text-sm font-medium">{reception.plant}</span>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="new-plant" className="w-28">
              Nueva planta:
            </Label>
            <Select value={plant} onValueChange={(v) => setPlant(v as Plant)}>
              <SelectTrigger id="new-plant" className="h-9 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLANT_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updatePlant.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={disabled}>
            {updatePlant.isPending ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
